import { FC, useEffect, useMemo, useState } from 'react';

import { useTour } from '@reactour/tour';
import { TbDotsVertical } from 'react-icons/tb';
import { capitalize, compact } from 'lodash';
import { dayjs } from 'utils/providers/Timezone';
import DOMPurify from 'dompurify';
import { NavAction } from 'ui/components/nav';
import { DeleteItemModal, Status } from 'ui/components/common';
import { addClassNameToRichText, cn } from 'utils/helpers';
import { Avatar } from 'ui/components/img';
import { useGetSubscription } from 'modules/account/settings/hooks';
import c, { SIZE } from 'utils/constants';
import { AvatarGroup } from 'ui/components/img/Avatar';
import useModal from 'ui/partials/Modal/useModal';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { Task } from 'utils/types';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { Link } from 'react-router-dom';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';

import { useTasksContext } from '../containers/Tasks';
import { useDeleteTask, useUpdateTaskStatus } from '../hooks';
import getPriorityStyle from '../utils/getPriorityStyle';
import MarkAsComplete from './MarkAsComplete';

const { ACTIONS, PERMISSION_KEYS } = c;

type PROPS = {
  task: Task;
  isLoading?: boolean;
  isArchived?: boolean;
};

const TaskCard: FC<PROPS> = ({
  task,
  isLoading = false,
  isArchived = false,
}) => {
  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.TASKS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.TASKS,
  );
  const { setSelectedTaskId, selectedTaskId } = useTasksContext();
  const { showModal } = useModal();
  const { isOpen, setCurrentStep, setSteps, setIsOpen } = useTour();
  const { data: subscription } = useGetSubscription();
  const { mutate: deleteTask, isLoading: deleteLoading } = useDeleteTask();
  const { mutate: updateStatus, isLoading: updateStatusLoading } =
    useUpdateTaskStatus();
  const {
    state: { relativePath, isPreview },
  } = useAppWrapperContext();

  const [isChecked, setIsChecked] = useState(
    task.status === 'COMPLETED' || task.status === 'ARCHIVED',
  );

  const leadLink = useMemo(() => {
    if (isPreview)
      return `/preview/${relativePath
        ?.split('/')
        .slice(2, relativePath?.split('/').length)
        .join('/')}contact/leads/${task?.lead?._id}`;

    return `/contact/leads/${task?.lead?._id}`;
  }, [relativePath, isPreview, task]);

  useEffect(() => {
    if (['COMPLETED', 'ARCHIVED'].includes(task?.status)) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [task?.status]);

  const options = useMemo(() => {
    const opts = [
      {
        text: 'View full details',
        key: 'view',
        onClick: () => {
          setSelectedTaskId?.(task._id);
          if (isOpen) {
            setTimeout(() => setCurrentStep(4), 500);
          }
        },
        'data-tut': TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.VIEW,
      },
      !subscription?.isExpired &&
        canDelete && {
          text: 'Delete',
          key: 'delete',
          onClick: () => {
            showModal({
              title: 'Delete Task',
              content: (
                <DeleteItemModal
                  onClickDelete={() => deleteTask(task._id)}
                  text="Task"
                  isLoading={deleteLoading}
                  validation
                />
              ),
            });
          },
        },
    ];

    if (canEdit && task.status === 'COMPLETED') {
      opts.push({
        text: 'Move to archived',
        key: 'archive',
        onClick: () => updateStatus({ id: task._id, status: 'archived' }),
      });
    }

    if (canEdit && task.status === 'ARCHIVED') {
      opts.push({
        text: 'Unarchive',
        key: 'unarchive',
        onClick: () => updateStatus({ id: task._id, status: 'completed' }),
      });
    }

    return compact(opts);
  }, [
    subscription?.isExpired,
    canDelete,
    canEdit,
    task.status,
    task._id,
    setSelectedTaskId,
    isOpen,
    setCurrentStep,
    showModal,
    deleteLoading,
    deleteTask,
    updateStatus,
  ]);

  const handleClickCheckbox = () => {
    if (isOpen) {
      setCurrentStep(0);
      setSteps?.([]);
      setIsOpen(false);
    } else {
      updateStatus({
        id: task._id,
        status: isChecked ? 'pending' : 'completed',
      });
    }
  };

  const taskPriority: 'HIGH' | 'MEDIUM' | 'LOW' | 'Done' = useMemo(() => {
    switch (task.status) {
      case 'COMPLETED':
        return 'Done';
      case 'ARCHIVED':
        return 'Done';

      default:
        return task.priority as 'HIGH' | 'MEDIUM' | 'LOW' | 'Done';
    }
  }, [task.priority, task.status]);

  const isOverdue = useMemo(
    () => dayjs(task.dueAt).diff(dayjs(new Date()), 'd') <= -1 && !isChecked,
    [task.dueAt, isChecked],
  );

  return (
    <div
      onClick={() => setSelectedTaskId?.((prev) => (!prev ? task._id : null))}
      className={cn(
        'cursor-pointer',
        'text-secondary-dark',
        'shadow-leapify-lg',
        'rounded-[6px] border-2',
        'divide-y-[0.5px]',
        'hover:border-2 hover:border-secondary-dark',
        selectedTaskId === task._id
          ? 'border-2 border-secondary-dark'
          : 'border-transparent',
        isChecked
          ? 'border-separator-secondary bg-separator-primary divide-separator-tertiary'
          : 'bg-fill-tertiary divide-separator-primary',
      )}
      key={task._id}
      data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.VIEW_UPDATED}
    >
      <div
        className={cn(
          'px-[18px] py-3 pr-[6px]',
          'flex items-center',
          isArchived || subscription?.isExpired
            ? 'justify-end'
            : 'justify-between',
        )}
      >
        {!isArchived && !subscription?.isExpired && canEdit && (
          <MarkAsComplete
            data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.COMPLETE_TASK}
            handleToggle={handleClickCheckbox}
            disabled={isLoading || updateStatusLoading}
            isComplete={isChecked}
          />
        )}
        <NavAction
          label={
            <TbDotsVertical
              className={cn(
                'mr-0 h-6 w-6',
                'text-secondary-dark hover:text-primary-base',
              )}
            />
          }
          options={options}
          optionsClassName="w-[190px]"
          data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.MANAGE}
        />
      </div>
      <div
        className={cn(
          'space-y-3 px-[18px] py-4 text-xs',
          isChecked && 'opacity-70',
        )}
      >
        <div className="flex items-center justify-between">
          <p className="font-semibold">{task.type}</p>
          <Status
            label={capitalize(taskPriority)}
            className={cn(
              'bg-transparent p-0 px-2 py-[3px]',
              'w-fit leading-5',
              getPriorityStyle(taskPriority),
            )}
          />
        </div>
        <div>
          <p
            className={cn(
              'max-w-full truncate text-lg font-semibold',
              isChecked && 'line-through',
            )}
          >
            {task.title}
          </p>
          <p
            className={cn(
              task.status === 'IN_PROGRESS' && 'text-info-base',
              'font-semibold',
            )}
          >
            {task.status === 'IN_PROGRESS'
              ? 'In Progress'
              : capitalize(task.status)}
          </p>
          <p
            className={cn(
              isOverdue ? 'text-danger-base' : 'text-content',
              'flex gap-x-1',
            )}
          >
            Due Date:{' '}
            {dayjs(task.dueAt).format(c.DATE_FORMAT_V2) +
              (isOverdue ? ' (Overdue)' : '')}
          </p>
        </div>
        {task.content && (
          <div
            className="h-9 overflow-y-hidden"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                addClassNameToRichText(
                  'line-clamp-2',
                  addClassNameToRichText('text-sm', task.content) as string,
                ) as string,
              ),
            }}
          />
        )}
        <Link
          to={leadLink}
          className={cn(
            'mt-1',
            'flex',
            'flex-1',
            'items-center',
            'gap-x-3',
            'cursor',
          )}
        >
          <Avatar size={SIZE.Medium} user={task?.lead} />
          <div
            className="w-full"
            title={`Associated Contact: ${task?.lead?.fullName}`}
          >
            <p className="line-clamp-1">Associated Contact</p>
            <p className="line-clamp-1 font-semibold">{task?.lead?.fullName}</p>
          </div>
        </Link>
        <div>
          <p className="mb-1">Assigned to</p>
          <AvatarGroup size={SIZE.Small} users={task.accountUsers} />
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
